import styled from "@emotion/styled";
import { IconButton } from "@leafygreen-ui/icon-button";
import { Menu, MenuItem } from "@leafygreen-ui/menu";
import Copy from "@via-ds/icons/Copy";
import Export from "@via-ds/icons/Export";
import MagnifyingGlass from "@via-ds/icons/MagnifyingGlass";
import Sparkle from "@via-ds/icons/Sparkle";
import Trash from "@via-ds/icons/Trash";
import pluralize from "pluralize";
import { useChatContext } from "@evg-ui/fungi";
import Icon from "@evg-ui/lib/components/Icon";
import { size } from "@evg-ui/lib/constants/tokens";
import { useToastContext } from "@evg-ui/lib/context/toast";
import { useQueryParams } from "@evg-ui/lib/hooks";
import {
  getLocalStorageString,
  setLocalStorageString,
} from "@evg-ui/lib/utils/localStorage";
import { copyToClipboard } from "@evg-ui/lib/utils/string";
import { useLogWindowAnalytics } from "analytics";
import { CopyFormat } from "constants/enums";
import { QueryParams, urlParseOptions } from "constants/queryParams";
import { COPY_FORMAT } from "constants/storageKeys";
import { useLogContext } from "context/LogContext";
import { useMultiLineSelectContext } from "context/MultiLineSelectContext";
import { useIsParsleyAIAvailable } from "hooks/useIsParsleyAIAvailable";
import { getJiraFormat, getRawLines } from "utils/string";
import { getLinesInProcessedLogLinesFromSelectedLines } from "./utils";

interface SharingMenuProps {
  lineNumber: number;
  shared?: boolean;
}

const SharingMenu: React.FC<SharingMenuProps> = ({ lineNumber, shared }) => {
  const {
    clearSelection,
    openMenu: open,
    selectedLines,
    setOpenMenu: setOpen,
  } = useMultiLineSelectContext();
  const { getLine, isUploadedLog, processedLogLines } = useLogContext();
  const { toggleChip } = useChatContext();
  const isParsleyAIAvailable = useIsParsleyAIAvailable();

  const [params, setParams] = useQueryParams(urlParseOptions);
  const dispatchToast = useToastContext();
  const { sendEvent } = useLogWindowAnalytics();
  const setMenuOpen = () => {
    sendEvent({ name: "Toggled share menu", open });
    setOpen(!open);
  };

  const handleAddToParsleyAI = async () => {
    const { endingLine, startingLine } = selectedLines;
    if (startingLine === undefined) return;

    const lineNumbers = getLinesInProcessedLogLinesFromSelectedLines(
      processedLogLines,
      selectedLines,
    );
    sendEvent({ name: "Clicked add to Parsley AI button" });
    setOpen(false);
    toggleChip({
      badgeLabel: endingLine
        ? `Lines ${startingLine}–${endingLine}`
        : `Line ${startingLine}`,
      content: getRawLines(lineNumbers, getLine),
      identifier: endingLine
        ? `lines-${startingLine}-to-${endingLine}`
        : `line-${startingLine}`,
      metadata: {
        endingLine,
        startingLine,
      },
    });
  };

  const handleCopySelectedLines = async () => {
    const { startingLine } = selectedLines;
    if (startingLine === undefined) return;
    // Create an array of line numbers that represent the range in selectedLines
    const lineNumbers = getLinesInProcessedLogLinesFromSelectedLines(
      processedLogLines,
      selectedLines,
    );

    const savedFormat = getLocalStorageString(COPY_FORMAT);
    const copyFormat =
      savedFormat === CopyFormat.Raw ? CopyFormat.Raw : CopyFormat.Jira;
    const getText = copyFormat === CopyFormat.Raw ? getRawLines : getJiraFormat;
    const formatLabel = copyFormat === CopyFormat.Raw ? "raw" : "Jira";

    await copyToClipboard(getText(lineNumbers, getLine));
    setLocalStorageString(COPY_FORMAT, copyFormat);
    setOpen(false);
    sendEvent({
      name: "Clicked copy share lines to clipboard button",
    });
    dispatchToast.success(
      `Copied ${pluralize("line", lineNumbers.length, true)} to clipboard (${formatLabel})`,
      true,
      { timeout: 5000 },
    );
  };

  const handleOnlySearchOnRange = () => {
    const { endingLine, startingLine } = selectedLines;
    if (startingLine === undefined) return;
    setParams({
      ...params,
      [QueryParams.LowerRange]: startingLine,
      [QueryParams.UpperRange]: endingLine ?? startingLine,
    });
    setOpen(false);
    sendEvent({
      name: "Used range limit for search",
    });
  };

  const handleShareLinkToSelectedLines = async () => {
    const { startingLine } = selectedLines;
    if (startingLine === undefined) return;
    const url = new URL(window.location.href);
    url.searchParams.set(QueryParams.ShareLine, startingLine.toString());

    await copyToClipboard(url.toString());
    setParams({ ...params, [QueryParams.ShareLine]: startingLine });
    setOpen(false);
    sendEvent({ name: "Clicked copy share link button" });
    dispatchToast.success("Copied link to clipboard", true, { timeout: 5000 });
  };

  const lineCount =
    selectedLines.endingLine === undefined ||
    selectedLines.startingLine === undefined
      ? 1
      : selectedLines.endingLine - selectedLines.startingLine + 1;

  return (
    <StyledMenu
      align="right"
      data-testid="sharing-menu"
      open={open}
      setOpen={setMenuOpen}
      trigger={
        <MenuIcon
          aria-label="Expand share menu"
          data-testid={`log-link-${lineNumber}`}
          onClick={setMenuOpen}
        >
          <Icon glyph={shared ? "ArrowWithCircle" : "Ellipsis"} />
        </MenuIcon>
      }
    >
      {isParsleyAIAvailable && (
        <MenuItem glyph={<Sparkle />} onClick={handleAddToParsleyAI}>
          Add to Parsley AI
        </MenuItem>
      )}
      <MenuItem glyph={<Copy />} onClick={handleCopySelectedLines}>
        Copy selected contents
      </MenuItem>
      {!isUploadedLog && (
        <MenuItem glyph={<Export />} onClick={handleShareLinkToSelectedLines}>
          Copy share link to selected {pluralize("line", lineCount)}
        </MenuItem>
      )}
      <MenuItem glyph={<MagnifyingGlass />} onClick={handleOnlySearchOnRange}>
        Only search on range
      </MenuItem>
      <MenuItem glyph={<Trash />} onClick={clearSelection}>
        Clear selection
      </MenuItem>
    </StyledMenu>
  );
};

const MenuIcon = styled(IconButton)`
  height: 16px;
  width: 16px;
  margin-left: ${size.xxs};
`;

const StyledMenu = styled(Menu)`
  width: fit-content;
`;
export default SharingMenu;
